/**
 * PWA install detection utilities.
 * Pure functions for platform, standalone, and dismissal state.
 */

export const PWA_INSTALL_STORAGE_KEY = 'pwa-install-dismissed';
/** Default: 0 = never re-show after dismiss. Set > 0 to re-show after N days. */
export const PWA_RE_SHOW_AFTER_DAYS = 0;

/**
 * Detects if the app is running in standalone (installed) mode.
 * Uses display-mode media query and iOS-specific navigator.standalone.
 * @returns {boolean}
 */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  const standaloneMedia = window.matchMedia('(display-mode: standalone)');
  const isStandaloneMode = standaloneMedia?.matches === true;
  const isIOSStandalone = !!navigator.standalone;
  return isStandaloneMode || isIOSStandalone;
}

/**
 * Detects if the device is iOS (iPhone, iPad, iPod).
 * @returns {boolean}
 */
export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Detects if the device is Android (user agent).
 * @returns {boolean}
 */
export function isAndroid() {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
}

/**
 * Platform for PWA install behavior: 'ios' | 'android' | 'desktop'.
 * Desktop = no install prompt (browser doesn't need "Add to Home Screen" in the same way).
 * @returns {'ios' | 'android' | 'desktop'}
 */
export function getPWAPlatform() {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'desktop';
}

/**
 * Whether we should offer the install prompt (mobile only).
 * @returns {boolean}
 */
export function isMobilePlatform() {
  const platform = getPWAPlatform();
  return platform === 'ios' || platform === 'android';
}

/**
 * Gets the timestamp when the user last dismissed the PWA install prompt.
 * @returns {number | null} Unix ms or null if never dismissed
 */
export function getDismissedAt() {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(PWA_INSTALL_STORAGE_KEY);
  if (!raw) return null;
  const ts = parseInt(raw, 10);
  return Number.isFinite(ts) ? ts : null;
}

/**
 * Persists the dismissal timestamp so we don't show the prompt again (or until re-show window).
 */
export function setDismissedNow() {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(PWA_INSTALL_STORAGE_KEY, String(Date.now()));
}

/**
 * Whether we are allowed to show the prompt again based on dismissal and optional cooldown.
 * @param {number} [reShowAfterDays=0] If > 0, re-show after this many days. 0 = never re-show after dismiss.
 * @returns {boolean}
 */
export function canShowAfterDismiss(reShowAfterDays = 0) {
  const dismissedAt = getDismissedAt();
  if (!dismissedAt) return true;
  if (reShowAfterDays <= 0) return false;
  const elapsed = Date.now() - dismissedAt;
  const cooldownMs = reShowAfterDays * 24 * 60 * 60 * 1000;
  return elapsed >= cooldownMs;
}
